import {LABEL_ARCHIVE_TYPE, type LabelArchiveType} from '../constants/values'

/**
 * Converts the `archived` URL parameter to a LabelArchiveType enum value.
 *
 * IMPORTANT: This helper should only be called when the labels_archiving feature flag is enabled.
 * When the feature is disabled, no archive filtering should be applied.
 *
 * @param archivedParam - The `archived` query parameter value ('true', 'false', or null)
 * @returns ARCHIVED when param is 'true', UNARCHIVED when 'false' or null (defaults to active labels)
 *
 * @example
 * // Only call when feature flag is enabled
 * if (isFeatureEnabled('labels_archiving')) {
 *   const archiveType = parseArchivedParam(searchParams.get('archived'))
 * }
 *
 * parseArchivedParam('true')   // => 'ARCHIVED'
 * parseArchivedParam('false')  // => 'UNARCHIVED'
 * parseArchivedParam(null)     // => 'UNARCHIVED' (default to active)
 */
export function parseArchivedParam(archivedParam: string | null): LabelArchiveType {
  const {ARCHIVED, UNARCHIVED} = LABEL_ARCHIVE_TYPE
  return archivedParam === 'true' ? ARCHIVED : UNARCHIVED
}

/**
 * Converts a LabelArchiveType enum value to the `archived` URL parameter string.
 *
 * @param archiveType - The archive type enum value
 * @returns 'true' for ARCHIVED, 'false' for UNARCHIVED
 *
 * @example
 * serializeArchivedParam('ARCHIVED')    // => 'true'
 * serializeArchivedParam('UNARCHIVED')  // => 'false'
 */
export function serializeArchivedParam(archiveType: LabelArchiveType): string {
  const {ARCHIVED} = LABEL_ARCHIVE_TYPE
  return archiveType === ARCHIVED ? 'true' : 'false'
}

/**
 * Checks if the `archived` URL parameter indicates archived labels.
 *
 * IMPORTANT: This helper should only be called when the labels_archiving feature flag is enabled.
 * When the feature is disabled, no archive filtering should be applied.
 *
 * @param archivedParam - The `archived` query parameter value ('true', 'false', or null)
 * @returns true when param is 'true', false when 'false' or null (defaults to active labels)
 *
 * @example
 * // Only call when feature flag is enabled
 * if (isFeatureEnabled('labels_archiving')) {
 *   const isArchived = isArchivedParam(searchParams.get('archived'))
 * }
 *
 * isArchivedParam('true')   // => true
 * isArchivedParam('false')  // => false
 * isArchivedParam(null)     // => false (default to active)
 */
export function isArchivedParam(archivedParam: string | null): boolean {
  return parseArchivedParam(archivedParam) === LABEL_ARCHIVE_TYPE.ARCHIVED
}
